import React, { useState, useEffect, useContext } from 'react';
import { 
  Container, Row, Col, Card, Badge, Button, Table, 
  Modal, Form, Alert, Spinner, Tabs, Tab, ProgressBar
} from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCalendar, faClock, faDollarSign, faUsers,
  faCheck, faTimes, faEye, faVideo, faHistory,
  faChartLine, faWallet, faUserMd, faCoins,
  faMessage, faFileAlt, faExclamationTriangle
} from '@fortawesome/free-solid-svg-icons';
import Header from '../components/header';
import Footer from '../components/footer';
import { MyContext } from '../MainContext';
import { backendUrl } from '../utils/variables';
import '../components/TherapyNew.css';

// Helper function to safely parse dates for Safari compatibility
const parseDate = (dateString) => {
  if (!dateString) return null;
  
  // Replace space with 'T' for ISO format compatibility
  const isoString = dateString.replace(' ', 'T');
  
  try {
    return new Date(isoString);
  } catch (error) {
    console.error('Error parsing date:', dateString, error);
    return null;
  }
};

const ClientDashboard = () => {
  const { user } = useContext(MyContext);
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [showSessionModal, setShowSessionModal] = useState(false);
  const [selectedSession, setSelectedSession] = useState(null);
  const [messageText, setMessageText] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);
  const [messages, setMessages] = useState([]);
  const [loadingMessages, setLoadingMessages] = useState(false);

  useEffect(() => {
    if (user) {
      loadClientData();
    }
  }, [user]);

  const loadClientData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Load client sessions
      const sessionsResponse = await fetch(`${backendUrl}/fetch_client_sessions.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ client_id: user.user_id })
      });
      
      const sessionsData = await sessionsResponse.json();

      if (sessionsData.success) {
        setSessions(sessionsData.sessions);
      } else {
        console.warn('Failed to load sessions:', sessionsData.messages);
      }

    } catch (err) {
      setError('Failed to load dashboard data: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const loadSessionMessages = async (sessionId) => {
    try {
      setLoadingMessages(true);
      
      const response = await fetch(`${backendUrl}/fetch_therapy_messages.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ session_id: sessionId })
      });
      
      const data = await response.json();
      
      if (data.success) {
        setMessages(data.messages);
      } else {
        console.warn('Failed to load messages:', data.messages);
      }
    } catch (err) {
      console.error('Error loading messages:', err);
    } finally {
      setLoadingMessages(false);
    }
  };

  const sendMessage = async () => {
    if (!messageText.trim() || !selectedSession) return;
    
    try {
      setSendingMessage(true);
      
      const response = await fetch(`${backendUrl}/send_therapy_message.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          session_id: selectedSession.session_id,
          sender_id: user.user_id,
          sender_type: 'user',
          message: messageText.trim()
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        setMessageText('');
        // Reload messages
        await loadSessionMessages(selectedSession.session_id);
      } else {
        setError(data.messages || 'Failed to send message');
      }
    } catch (err) {
      setError('Failed to send message: ' + err.message);
    } finally {
      setSendingMessage(false);
    }
  };

  const getSessionStatusBadge = (status) => {
    const variants = {
      'pending': 'warning',
      'approved': 'success',
      'completed': 'info',
      'cancelled': 'danger',
      'paid': 'success'
    };
    return <Badge bg={variants[status] || 'secondary'}>{status}</Badge>;
  };

  const formatCurrency = (amount) => {
    return `₦${parseFloat(amount || 0).toFixed(2)}`;
  };

  const formatCoins = (coins) => {
    return `${coins?.toLocaleString() || '0'} coins`;
  };

  const formatCoinsWithNaira = (coins) => {
    const nairaAmount = (coins || 0) * 100;
    return `${coins?.toLocaleString() || '0'} coins (₦${nairaAmount.toLocaleString()})`;
  };

  const getActiveSession = () => {
    return sessions.find(s => s.status === 'approved' || s.status === 'active');
  };

  const canRequestNewSession = () => {
    return !getActiveSession();
  };

  if (loading) {
    return (
      <>
        <Header />
        <Container className="py-5 text-center">
          <Spinner animation="border" variant="primary" />
          <p className="mt-3">Loading dashboard...</p>
        </Container>
        <Footer />
      </>
    );
  }

  const pendingSessions = sessions.filter(s => s.status === 'pending');
  const approvedSessions = sessions.filter(s => s.status === 'approved');
  const completedSessions = sessions.filter(s => s.status === 'completed');
  const activeSession = getActiveSession();

  return (
    <>
      <Header />
      
      <Container fluid className="py-4">
        <Row className="mb-4">
          <Col>
            <h2>
              <FontAwesomeIcon icon={faUserMd} className="me-2 text-primary" />
              Client Dashboard
            </h2>
            <p className="text-muted">Track your therapy sessions and communicate with therapists</p>
          </Col>
        </Row>

        {error && (
          <Alert variant="danger" dismissible onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Active Session Alert */}
        {activeSession && (
          <Alert variant="info" className="mb-4">
            <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
            <strong>Active Session:</strong> You have an active session with {activeSession.therapist_name}. 
            You can only have one active session at a time.
          </Alert>
        )}

        {/* New Session Request Alert */}
        {!canRequestNewSession() && (
          <Alert variant="warning" className="mb-4">
            <FontAwesomeIcon icon={faClock} className="me-2" />
            <strong>Session Limit:</strong> You currently have an active session. 
            You can request a new session once your current session is completed.
          </Alert>
        )}

        <Row className="mb-4">
          <Col md={3}>
            <Card className="text-center">
              <Card.Body>
                <FontAwesomeIcon icon={faClock} size="2x" className="text-warning mb-2" />
                <h4>{pendingSessions.length}</h4>
                <p className="text-muted">Pending Requests</p>
              </Card.Body>
            </Card>
          </Col>
          
          <Col md={3}>
            <Card className="text-center">
              <Card.Body>
                <FontAwesomeIcon icon={faCalendar} size="2x" className="text-success mb-2" />
                <h4>{approvedSessions.length}</h4>
                <p className="text-muted">Approved Sessions</p>
              </Card.Body>
            </Card>
          </Col>
          
          <Col md={3}>
            <Card className="text-center">
              <Card.Body>
                <FontAwesomeIcon icon={faCheck} size="2x" className="text-info mb-2" />
                <h4>{completedSessions.length}</h4>
                <p className="text-muted">Completed Sessions</p>
              </Card.Body>
            </Card>
          </Col>
          
          <Col md={3}>
            <Card className="text-center">
              <Card.Body>
                <FontAwesomeIcon icon={faCoins} size="2x" className="text-success mb-2" />
                <h4>{formatCoins(sessions.reduce((total, s) => total + parseFloat(s.coins_paid || 0), 0))}</h4>
                <p className="text-muted">Total Spent</p>
                <small className="text-muted">
                  (₦{(sessions.reduce((total, s) => total + parseFloat(s.coins_paid || 0), 0) * 100).toLocaleString()})
                </small>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        <Tabs activeKey={activeTab} onSelect={(k) => setActiveTab(k)} className="mb-4">
          <Tab eventKey="overview" title="Overview">
            <Row>
              <Col md={8}>
                <Card>
                  <Card.Header>
                    <h5 className="mb-0">Recent Session Requests</h5>
                  </Card.Header>
                  <Card.Body>
                    {sessions.length === 0 ? (
                      <p className="text-muted">No therapy sessions yet</p>
                    ) : (
                      <div>
                        {sessions.slice(0, 5).map((session) => (
                          <div key={session.session_id} className="border-bottom pb-2 mb-2">
                            <div className="d-flex justify-content-between align-items-start">
                              <div>
                                <strong>{session.therapist_name}</strong>
                                <br />
                                <small className="text-muted">{session.issue_text}</small>
                                <br />
                                <small className="text-muted">
                                  {formatCoins(session.session_price)}
                                </small>
                              </div>
                              <div className="text-end">
                                {getSessionStatusBadge(session.status)}
                                <br />
                                <Button
                                  size="sm"
                                  variant="outline-primary"
                                  className="mt-1"
                                  onClick={() => {
                                    setSelectedSession(session);
                                    setShowSessionModal(true);
                                    if (session.status === 'approved' || session.status === 'active') {
                                      loadSessionMessages(session.session_id);
                                    }
                                  }}
                                >
                                  <FontAwesomeIcon icon={faEye} className="me-1" />
                                  View
                                </Button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </Card.Body>
                </Card>
              </Col>
              
              <Col md={4}>
                <Card>
                  <Card.Header>
                    <h5 className="mb-0">Session Summary</h5>
                  </Card.Header>
                  <Card.Body>
                    <div className="mb-3">
                      <div className="d-flex justify-content-between">
                        <span>Total Sessions:</span>
                        <strong>{sessions.length}</strong>
                      </div>
                    </div>
                    
                    <div className="mb-3">
                      <div className="d-flex justify-content-between">
                        <span>Active Session:</span>
                        <strong>{activeSession ? 'Yes' : 'No'}</strong>
                      </div>
                    </div>
                    
                    <div className="mb-3">
                      <div className="d-flex justify-content-between">
                        <span>Can Request New:</span>
                        <strong>{canRequestNewSession() ? 'Yes' : 'No'}</strong>
                      </div>
                    </div>
                    
                    <div className="mb-3">
                      <div className="d-flex justify-content-between">
                        <span>Total Spent:</span>
                        <strong>{formatCoins(sessions.reduce((total, s) => total + parseFloat(s.coins_paid || 0), 0))}</strong>
                      </div>
                      <small className="text-muted">
                        (₦{(sessions.reduce((total, s) => total + parseFloat(s.coins_paid || 0), 0) * 100).toLocaleString()})
                      </small>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </Tab>

          <Tab eventKey="sessions" title="All Sessions">
            <Card>
              <Card.Header>
                <h5 className="mb-0">Session Management</h5>
              </Card.Header>
              <Card.Body>
                <Table responsive striped>
                  <thead>
                    <tr>
                      <th>Therapist</th>
                      <th>Issue</th>
                      <th>Status</th>
                      <th>Payment</th>
                      <th>Amount</th>
                      <th>Date</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sessions.length > 0 ? (
                      sessions.map((session) => (
                        <tr key={session.session_id}>
                          <td>{session.therapist_name}</td>
                          <td>
                            <div className="text-truncate" style={{ maxWidth: '200px' }}>
                              {session.issue_text}
                            </div>
                          </td>
                          <td>{getSessionStatusBadge(session.status)}</td>
                          <td>
                            {session.payment_status === 'paid' ? (
                              <Badge bg="success">Paid</Badge>
                            ) : (
                              <Badge bg="warning">Pending</Badge>
                            )}
                          </td>
                          <td>{formatCoins(session.session_price)}</td>
                          <td>{parseDate(session.created_at)?.toLocaleDateString() || 'Invalid date'}</td>
                          <td>
                            <Button
                              size="sm"
                              variant="outline-primary"
                              onClick={() => {
                                setSelectedSession(session);
                                setShowSessionModal(true);
                                if (session.status === 'approved' || session.status === 'active') {
                                  loadSessionMessages(session.session_id);
                                }
                              }}
                            >
                              <FontAwesomeIcon icon={faEye} className="me-1" />
                              View
                            </Button>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="7" className="text-center text-muted">
                          No sessions found
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </Card.Body>
            </Card>
          </Tab>
        </Tabs>
      </Container>

      {/* Session Detail Modal */}
      <Modal show={showSessionModal} onHide={() => setShowSessionModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Session Details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedSession && (
            <div>
              <Row>
                <Col md={6}>
                  <h6>Therapist Information</h6>
                  <p><strong>Name:</strong> {selectedSession.therapist_name}</p>
                  <p><strong>Email:</strong> {selectedSession.therapist_email}</p>
                  <p><strong>Requested:</strong> {parseDate(selectedSession.created_at)?.toLocaleString() || 'Invalid date'}</p>
                </Col>
                <Col md={6}>
                  <h6>Session Information</h6>
                  <p><strong>Status:</strong> {getSessionStatusBadge(selectedSession.status)}</p>
                  <p><strong>Payment:</strong> {getSessionStatusBadge(selectedSession.payment_status)}</p>
                  <p><strong>Amount:</strong> {formatCoins(selectedSession.session_price)}</p>
                  {selectedSession.scheduled_time && (
                    <p><strong>Scheduled:</strong> {parseDate(selectedSession.scheduled_time)?.toLocaleString() || 'Invalid date'}</p>
                  )}
                </Col>
              </Row>
              
              <hr />
              
              <h6>Your Issue</h6>
              <p>{selectedSession.issue_text}</p>
              
              {/* Chat Section for Active Sessions */}
              {(selectedSession.status === 'approved' || selectedSession.status === 'active') && (
                <>
                  <hr />
                  <h6>
                    <FontAwesomeIcon icon={faMessage} className="me-2" />
                    Session Chat
                  </h6>
                  
                  <div className="border rounded p-3 mb-3" style={{ height: '300px', overflowY: 'auto' }}>
                    {loadingMessages ? (
                      <div className="text-center">
                        <Spinner animation="border" size="sm" />
                        <p className="mt-2">Loading messages...</p>
                      </div>
                    ) : messages.length > 0 ? (
                      messages.map((message, index) => (
                        <div key={index} className={`mb-2 ${message.sender_type === 'client' ? 'text-end' : 'text-start'}`}>
                          <div className={`d-inline-block p-2 rounded ${message.sender_type === 'client' ? 'bg-primary text-white' : 'bg-light'}`}>
                            <small className="d-block">{message.sender_name}</small>
                            {message.message}
                            <br />
                            <small className="opacity-75">
                              {new Date(message.created_at).toLocaleString()}
                            </small>
                          </div>
                        </div>
                      ))
                    ) : (
                      <p className="text-muted text-center">No messages yet. Start the conversation!</p>
                    )}
                  </div>
                  
                  <Form.Group>
                    <Form.Control
                      as="textarea"
                      rows={3}
                      placeholder="Type your message..."
                      value={messageText}
                      onChange={(e) => setMessageText(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && !e.shiftKey && sendMessage()}
                    />
                  </Form.Group>
                  
                  <Button
                    variant="primary"
                    onClick={sendMessage}
                    disabled={sendingMessage || !messageText.trim()}
                    className="mt-2"
                  >
                    {sendingMessage ? (
                      <Spinner animation="border" size="sm" />
                    ) : (
                      <>
                        <FontAwesomeIcon icon={faMessage} className="me-1" />
                        Send Message
                      </>
                    )}
                  </Button>
                </>
              )}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowSessionModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      <Footer />
    </>
  );
};

export default ClientDashboard; 